/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Download, FileText } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { intakeApi } from '../../lib/api/intake.js';
import { useIntakeDocumentMeta } from '../../lib/queries/intake.js';

import { INTAKE_CARD_CLASS } from './intake-form-utils.js';

/** Trigger a browser download of the generated intake PDF. */
async function downloadIntake(clientId: string, fileName: string): Promise<void> {
  const blob = await intakeApi.downloadDocument(clientId);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download-only view of the admin-generated intake PDF — for roles that may read
 * the report but not generate/sign it (assigned therapist, parent). Shows a muted
 * note while the clinic has not produced the report yet.
 */
export function IntakeReportDownload({ clientId }: { readonly clientId: string }): JSX.Element {
  const { t } = useTranslation();
  const meta = useIntakeDocumentMeta(clientId, false);
  const available = meta.data?.available ?? false;
  const onDownload = useCallback(() => {
    void downloadIntake(clientId, meta.data?.fileName ?? 'patient-intake.pdf');
  }, [clientId, meta.data]);

  return (
    <div className={INTAKE_CARD_CLASS}>
      <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
        <FileText className="h-4 w-4 text-[var(--accent)]" aria-hidden="true" />
        {t('intake.report.title')}
      </h3>
      <p className="mb-4 text-sm text-slate-400">{t('intake.report.subtitle')}</p>
      {available ? (
        <Button type="button" variant="outline" onClick={onDownload}>
          <Download className="h-4 w-4" aria-hidden="true" />
          {t('intake.report.download')}
        </Button>
      ) : (
        <p className="text-sm text-slate-400">{t('intake.report.notReady')}</p>
      )}
    </div>
  );
}
